<template>
  <transition name="job-family-picker-dialog" appear>
    <div
      v-if="open && featureEnabled"
      class="job-family-picker-dialog__overlay"
      :class="{ 'job-family-picker-dialog__overlay--stacked': stackAboveModal }"
      role="dialog"
      aria-modal="true"
      aria-labelledby="job-family-picker-dialog-title"
      @mousedown.self="handleDismiss"
    >
      <div class="job-family-picker-dialog__card">
        <div class="job-family-picker-dialog__header">
          <div class="job-family-picker-dialog__header-icon" aria-hidden="true">
            <JobFamilyIcon size="normal" />
          </div>
          <button
            type="button"
            class="job-family-picker-dialog__close-btn"
            aria-label="Close"
            @click="handleDismiss"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="job-family-picker-dialog__intro">
          <span id="job-family-picker-dialog-title" class="job-family-picker-dialog__title">
            {{ title }}
          </span>
          <span class="job-family-picker-dialog__subtitle">
            {{ subtitle }}
          </span>
        </div>

        <!-- Step 3: full path committed -->
        <div v-if="selectedFamily && selectedLevel" class="job-family-picker-dialog__picked-wrap">
          <div class="job-family-picker-dialog__picked">
            <JobFamilyIcon size="normal" class="job-family-picker-dialog__picked-icon" />
            <span class="job-family-picker-dialog__breadcrumb">
              <span class="job-family-picker-dialog__breadcrumb-ancestor">
                {{ selectedFamily.jobFamilyName }}
              </span>
              <span class="job-family-picker-dialog__breadcrumb-sep">›</span>
              <span class="job-family-picker-dialog__breadcrumb-leaf">
                {{ formatLevelLabel(selectedLevel) }}
              </span>
            </span>
            <div
              class="job-family-picker-dialog__clear"
              role="button"
              tabindex="0"
              aria-label="Clear selection"
              @click.stop="clearFullSelection"
              @keydown.enter.stop="clearFullSelection"
              @keydown.space.stop.prevent="clearFullSelection"
            >
              <Icon name="x_close_icon" />
            </div>
          </div>
        </div>

        <!-- Step 2: Family picked, choose its Level -->
        <template v-else-if="selectedFamily">
          <div class="job-family-picker-dialog__picked-wrap">
            <div class="job-family-picker-dialog__picked job-family-picker-dialog__picked--family-only">
              <JobFamilyIcon size="normal" class="job-family-picker-dialog__picked-icon" />
              <span class="job-family-picker-dialog__breadcrumb-ancestor">
                {{ selectedFamily.jobFamilyName }}
              </span>
              <div
                class="job-family-picker-dialog__clear"
                role="button"
                tabindex="0"
                aria-label="Choose a different job family"
                @click.stop="clearFamilySelection"
                @keydown.enter.stop="clearFamilySelection"
                @keydown.space.stop.prevent="clearFamilySelection"
              >
                <Icon name="x_close_icon" />
              </div>
            </div>
          </div>

          <div class="job-family-picker-dialog__content">
            <div
              v-if="showSelectedFamilyNoLevels"
              class="job-family-picker-dialog__empty-state"
              role="status"
              data-testid="job-family-picker-no-levels"
            >
              <span class="job-family-picker-dialog__empty-title">
                Please add levels to this job family
              </span>
              <span class="job-family-picker-dialog__empty-subtitle">
                A position must be assigned to a family and a level together.
              </span>
            </div>
            <ul
              v-else
              class="job-family-picker-dialog__list"
              role="listbox"
              aria-label="Levels"
            >
              <li
                v-for="level in sortedSelectedFamilyLevels"
                :key="level.id"
                role="option"
                tabindex="0"
                :aria-selected="false"
                class="job-family-picker-dialog__row"
                @click="selectLevel(level)"
                @keydown.enter="selectLevel(level)"
                @keydown.space.prevent="selectLevel(level)"
              >
                <JobFamilyIcon size="normal" class="job-family-picker-dialog__row-icon" />
                <span class="job-family-picker-dialog__row-path">
                  <span class="job-family-picker-dialog__row-path-ancestor">
                    {{ selectedFamily.jobFamilyName }}
                  </span>
                  <span class="job-family-picker-dialog__row-path-sep">›</span>
                  <span class="job-family-picker-dialog__row-path-leaf">
                    {{ formatLevelLabel(level) }}
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </template>

        <!-- Step 1: search for a Family -->
        <template v-else>
          <div class="job-family-picker-dialog__search-wrap">
            <ExTextInput
              ref="searchInputRef"
              label=""
              ghostText="Search job families by name or code"
              icon="interface_search"
              clearText="clear search"
              class="job-family-picker-dialog__search-input"
              :value="searchQuery"
              @input="handleSearchInput"
            />
          </div>

          <div
            class="job-family-picker-dialog__content"
            :class="{ 'job-family-picker-dialog__content--list': showFamilyResults }"
          >
            <div v-if="showDefaultState" class="job-family-picker-dialog__empty-state">
              <span class="job-family-picker-dialog__empty-title">
                Start typing to find a job family
              </span>
              <span class="job-family-picker-dialog__empty-subtitle"> Search by name or code </span>
            </div>

            <ul
              v-else-if="showSkeleton"
              class="job-family-picker-dialog__skeleton-list"
              aria-hidden="true"
            >
              <li
                v-for="(barWidth, index) in skeletonBarWidths"
                :key="`skeleton-${index}`"
                class="job-family-picker-dialog__skeleton-row"
              >
                <span class="job-family-picker-dialog__skeleton-icon" />
                <span class="job-family-picker-dialog__skeleton-bar" :style="{ width: barWidth }" />
              </li>
            </ul>

            <div v-else-if="showNoResults" class="job-family-picker-dialog__empty-state">
              <span class="job-family-picker-dialog__empty-title">No matches</span>
              <span class="job-family-picker-dialog__empty-subtitle">
                Try a different name or code
              </span>
            </div>

            <ul
              v-else
              class="job-family-picker-dialog__list"
              role="listbox"
              aria-label="Job Families"
              :style="familyListStyle"
            >
              <li
                v-for="family in results"
                :key="family.jobFamilyId"
                role="option"
                tabindex="0"
                :aria-selected="false"
                class="job-family-picker-dialog__row"
                @click="selectFamily(family)"
                @keydown.enter="selectFamily(family)"
                @keydown.space.prevent="selectFamily(family)"
              >
                <JobFamilyIcon size="normal" class="job-family-picker-dialog__row-icon" />
                <span class="job-family-picker-dialog__row-path">
                  <span class="job-family-picker-dialog__row-path-leaf">
                    {{ family.jobFamilyName }}
                  </span>
                </span>
                <span class="job-family-picker-dialog__row-code">#{{ family.code }}</span>
              </li>
            </ul>
          </div>
        </template>

        <div class="job-family-picker-dialog__footer">
          <p
            v-if="error"
            class="job-family-picker-dialog__error"
            role="alert"
            data-testid="job-family-picker-dialog-error"
          >
            {{ error }}
          </p>
          <div class="job-family-picker-dialog__footer-actions">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Cancel"
              @click="handleDismiss"
            />
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              :label="busy ? busyConfirmLabel : confirmLabel"
              :disabled="!canAssign"
              :loaderLeft="busy"
              @click="handleAssign"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted, nextTick } from "vue";
import Icon from "@components/icons/Icon.vue";
import JobFamilyIcon from "@components/icons/items/OrgObject/JobFamilyIcon.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import {
  sortLevelsDescending,
  formatLevelLabel as formatLevelLabelUtil,
  findFamilyById,
  findLevelById,
} from "./jobFamilyPickerDialogUtils.js";

/**
 * JobFamilyPickerDialog — ER-11645. Single self-contained "Assign Job Family" picker
 * on the position form: the user always picks the whole Family + Level path in one
 * component (no separate composed PickerField). Modelled on DepartmentPickerDialog
 * for header, search behaviour, row treatment and Cancel/Assign footer; the path is
 * always exactly two segments since a Family is never nested (rules.md §14).
 *
 * Fully controlled: `results` (pre-filtered to Draft/Active families by the caller)
 * and `loading` drive the search step; the component never fetches or filters data
 * itself. `defaultExpanded` (ER-11838) makes the Family list browse on open instead
 * of showing the "Start typing" state — it emits `search("")` on open so the caller
 * can load the default list; `visibleRowCount` caps the Family list height. Behind
 * the idea-MDP-581 kill switch shared with the rest of Job Families.
 */
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Object,
    default: null,
  },
  results: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  busy: {
    type: Boolean,
    default: false,
  },
  /**
   * When true, the family list is expanded/populated on open (as if the search
   * field were focused with an empty query) instead of showing the "Start typing"
   * empty state. Reusable opt-in — defaults to false so other consumers keep the
   * legacy type-to-search behaviour. Only the Family step is affected; the Level
   * step is always unchanged.
   */
  defaultExpanded: {
    type: Boolean,
    default: false,
  },
  /**
   * Number of Family rows to keep visible at once (the rest scroll). When null the
   * list keeps its default max-height. Applied to the Family list only.
   */
  visibleRowCount: {
    type: Number,
    default: null,
  },
  title: {
    type: String,
    default: "Assign Job Family",
  },
  subtitle: {
    type: String,
    default: "Choose the Job Family and Level this position will belong to.",
  },
  confirmLabel: {
    type: String,
    default: "Assign",
  },
  busyConfirmLabel: {
    type: String,
    default: "Assigning...",
  },
  /** Raise overlay above nested Vuetify v-dialog modals (e.g. Pay Range wizard). */
  stackAboveModal: {
    type: Boolean,
    default: false,
  },
  /** Inline server error (Rule 10): shown in the footer while the dialog stays open. */
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:open", "update:modelValue", "search", "assign", "cancel"]);

const proxy = getCurrentInstance()?.proxy;
const featureEnabled = ref(true);

const searchQuery = ref("");
const selectedFamily = ref(null);
const selectedLevel = ref(null);
const searchInputRef = ref(null);
const skeletonBarWidths = ["62%", "38%", "78%", "55%", "44%"];
let searchDebounceTimeout = null;

const trimmedSearchQuery = computed(() => searchQuery.value.trim());
const hasQuery = computed(() => !!trimmedSearchQuery.value);

// The results list shows either while filtering (query present) or, when the
// dialog opts into default expansion, while browsing with an empty query.
const showFamilyResults = computed(
  () => !props.loading && props.results.length > 0 && (hasQuery.value || props.defaultExpanded)
);

const showDefaultState = computed(
  () => !hasQuery.value && !props.loading && !showFamilyResults.value
);
const showSkeleton = computed(() => props.loading && (hasQuery.value || props.defaultExpanded));
const showNoResults = computed(
  () => hasQuery.value && !props.loading && !props.results.length
);

// A Family row is 40px tall (10px padding + 20px line-height) with a 4px
// inter-row gap; the last row has no gap. `visibleRowCount` rows are therefore
// `n * 44 - 4` px tall.
const ROW_STRIDE_PX = 44;
const familyListStyle = computed(() =>
  props.visibleRowCount
    ? { maxHeight: `${props.visibleRowCount * ROW_STRIDE_PX - 4}px` }
    : null
);

const sortedSelectedFamilyLevels = computed(() =>
  sortLevelsDescending(selectedFamily.value?.levels)
);

const showSelectedFamilyNoLevels = computed(
  () => !!selectedFamily.value && sortedSelectedFamilyLevels.value.length === 0
);

const canAssign = computed(
  () => !!selectedFamily.value && !!selectedLevel.value && !props.busy
);

function formatLevelLabel(level) {
  return formatLevelLabelUtil(level);
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      hydrateFromModelValue();
      // Default expansion: ask the caller for the initial (empty-query) family list
      // so the dialog can browse before typing. Only the Family step (no
      // pre-selected family) is affected; the Level step keeps its behaviour.
      if (props.defaultExpanded && !selectedFamily.value) {
        emit("search", "");
      }
      nextTick(() => {
        if (!selectedFamily.value) {
          searchInputRef.value?.focus();
        }
      });
    } else {
      resetState();
    }
  },
  { immediate: true }
);

function hydrateFromModelValue() {
  searchQuery.value = "";
  clearDebounce();
  const family = findFamilyById(props.results, props.modelValue?.jobFamilyId);
  const level = findLevelById(family, props.modelValue?.levelId);
  selectedFamily.value = family;
  selectedLevel.value = level;
}

function clearDebounce() {
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
    searchDebounceTimeout = null;
  }
}

function resetState() {
  searchQuery.value = "";
  selectedFamily.value = null;
  selectedLevel.value = null;
  clearDebounce();
}

function selectFamily(family) {
  selectedFamily.value = family;
  selectedLevel.value = null;
  searchQuery.value = "";
  clearDebounce();
}

function clearFamilySelection() {
  selectedFamily.value = null;
  selectedLevel.value = null;
  emit("update:modelValue", null);
  nextTick(() => {
    searchInputRef.value?.focus();
  });
}

function clearFullSelection() {
  selectedFamily.value = null;
  selectedLevel.value = null;
  emit("update:modelValue", null);
  nextTick(() => {
    searchInputRef.value?.focus();
  });
}

function selectLevel(level) {
  if (!selectedFamily.value) return;
  selectedLevel.value = level;
  emit("update:modelValue", {
    jobFamilyId: selectedFamily.value.jobFamilyId,
    levelId: level.id,
  });
}

function handleSearchInput(value) {
  searchQuery.value = value ?? "";
  clearDebounce();

  const query = trimmedSearchQuery.value;
  if (!query) {
    emit("search", "");
    return;
  }

  searchDebounceTimeout = setTimeout(() => {
    emit("search", query);
    searchDebounceTimeout = null;
  }, 300);
}

function handleDismiss() {
  resetState();
  emit("update:open", false);
  emit("cancel");
}

function handleAssign() {
  if (!canAssign.value) return;
  emit("assign", {
    jobFamilyId: selectedFamily.value.jobFamilyId,
    levelId: selectedLevel.value.id,
  });
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.open) {
    handleDismiss();
  }
}

onMounted(async () => {
  document.addEventListener("keydown", handleKeydown);
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  clearDebounce();
});
</script>

<style scoped>
.job-family-picker-dialog-enter-active,
.job-family-picker-dialog-leave-active {
  transition: opacity 180ms ease;
}

.job-family-picker-dialog-enter-from,
.job-family-picker-dialog-leave-to {
  opacity: 0;
}

.job-family-picker-dialog-enter-active .job-family-picker-dialog__card,
.job-family-picker-dialog-leave-active .job-family-picker-dialog__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.job-family-picker-dialog-enter-from .job-family-picker-dialog__card,
.job-family-picker-dialog-leave-to .job-family-picker-dialog__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .job-family-picker-dialog-enter-active,
  .job-family-picker-dialog-leave-active,
  .job-family-picker-dialog-enter-active .job-family-picker-dialog__card,
  .job-family-picker-dialog-leave-active .job-family-picker-dialog__card {
    transition: none !important;
  }
}

.job-family-picker-dialog__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.job-family-picker-dialog__overlay--stacked {
  z-index: 10002;
}

.job-family-picker-dialog__card {
  width: 100%;
  max-width: 788px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  overflow: hidden;
}

.job-family-picker-dialog__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 0;
}

.job-family-picker-dialog__header-icon {
  display: flex;
  width: 48px;
  height: 48px;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  border: 1px solid #e9d7fe;
  background: #f9f5ff;
  box-shadow: 0 6px 16px -6px rgba(127, 86, 217, 0.25);
}

.job-family-picker-dialog__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 8px;
  margin: -4px -4px 0 0;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 8px;
}

.job-family-picker-dialog__close-btn:hover {
  background: rgba(152, 162, 179, 0.1);
}

.job-family-picker-dialog__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.job-family-picker-dialog__intro {
  display: flex;
  flex-direction: column;
  padding: 16px 24px 0;
}

.job-family-picker-dialog__title {
  color: #101828;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
}

.job-family-picker-dialog__subtitle {
  color: #667085;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.job-family-picker-dialog__search-wrap,
.job-family-picker-dialog__picked-wrap {
  padding: 16px 24px 0;
}

.job-family-picker-dialog__search-wrap :deep(.main_label) {
  display: none;
}

.job-family-picker-dialog__search-wrap :deep(.ex_text_input .input-search) {
  margin-top: 0 !important;
}

.job-family-picker-dialog__content {
  min-height: 220px;
  padding: 16px 24px 0;
}

.job-family-picker-dialog__content--list {
  min-height: 0;
  padding-bottom: 0;
}

.job-family-picker-dialog__empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  text-align: center;
}

.job-family-picker-dialog__empty-title {
  display: block;
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.job-family-picker-dialog__empty-subtitle {
  display: block;
  margin-top: 4px;
  color: #98a2b3;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.job-family-picker-dialog__skeleton-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.job-family-picker-dialog__skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
}

.job-family-picker-dialog__skeleton-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f2f4f7 0%, #eaecf0 50%, #f2f4f7 100%);
  background-size: 200% 100%;
  animation: job-family-picker-dialog-shimmer 1.4s ease-in-out infinite;
}

.job-family-picker-dialog__skeleton-bar {
  display: block;
  height: 12px;
  max-width: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #f2f4f7 0%, #eaecf0 50%, #f2f4f7 100%);
  background-size: 200% 100%;
  animation: job-family-picker-dialog-shimmer 1.4s ease-in-out infinite;
}

@keyframes job-family-picker-dialog-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.job-family-picker-dialog__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
}

.job-family-picker-dialog__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 0;
  cursor: pointer;
  box-sizing: border-box;
  margin-bottom: 4px;
  border-radius: 6px;
}

.job-family-picker-dialog__row:last-child {
  margin-bottom: 0;
}

.job-family-picker-dialog__row:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

.job-family-picker-dialog__row-icon {
  flex-shrink: 0;
  opacity: 0.6;
}

.job-family-picker-dialog__row-path {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
}

.job-family-picker-dialog__row-path-ancestor {
  color: #667085;
  font-weight: 400;
}

.job-family-picker-dialog__row-path-sep {
  color: #d0d5dd;
  font-weight: 400;
  margin: 0 6px;
}

.job-family-picker-dialog__row-path-leaf {
  color: #101828;
  font-weight: 500;
}

.job-family-picker-dialog__row-code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  color: #98a2b3;
  line-height: 20px;
  white-space: nowrap;
}

.job-family-picker-dialog__picked {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.job-family-picker-dialog__picked-icon {
  flex-shrink: 0;
}

.job-family-picker-dialog__breadcrumb {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  font-size: 14px;
  line-height: 20px;
}

.job-family-picker-dialog__breadcrumb-ancestor {
  flex: 1;
  min-width: 0;
  color: #475467;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
}

.job-family-picker-dialog__picked--family-only .job-family-picker-dialog__breadcrumb-ancestor {
  flex: 1;
}

.job-family-picker-dialog__breadcrumb-sep {
  color: #d0d5dd;
  font-weight: 400;
  margin: 0 4px;
}

.job-family-picker-dialog__breadcrumb-leaf {
  color: #6941c6;
  font-weight: 600;
}

.job-family-picker-dialog__clear {
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #98a2b3;
  padding: 2px;
  border-radius: 4px;
}

.job-family-picker-dialog__clear:hover {
  color: #667085;
  background: #f2f4f7;
}

.job-family-picker-dialog__clear:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.job-family-picker-dialog__footer {
  margin-top: 24px;
  padding: 16px 24px 24px;
  border-top: 1px solid #e5e7eb;
}

.job-family-picker-dialog__error {
  margin: 0 0 12px;
  color: #b42318;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.job-family-picker-dialog__footer-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}

.job-family-picker-dialog__footer-actions :deep(.standard-button) {
  flex: 1 1 0;
  min-width: 0;
}

.job-family-picker-dialog__footer-actions :deep(.standard-button button) {
  width: 100%;
}
</style>
